import { HTTPError, TimeoutError } from "ky";

import type { CaptureException, SentryInstance } from "./sentryClient.ts";

import {
  isError,
  returnError,
  throwError,
  toUnknownError,
  UnknownError,
} from "./errorHandling.ts";
import { getSentry } from "./sentryClient.ts";

/**
 * Wraps generic fetch failures that aren't HTTP errors.
 * Used for network failures, CORS issues, or load failures.
 * @example throw new FetchError(new Error('Failed to fetch'));
 */
class FetchError extends Error {
  constructor(cause: Error) {
    super(cause.message);
    this.name = "FetchError";
    this.cause = cause;
  }
}

/**
 * Base class for HTTP 4xx client errors.
 * Extended by specific error types like AuthError, NotFoundError, ValidationError.
 * @example throw new ClientError(httpError);
 */
class ClientError extends HTTPError {
  constructor(cause: HTTPError) {
    super(cause.response, cause.request, cause.options);
    this.name = "ClientError";
  }
}

/**
 * HTTP 401/403 authentication or authorization errors.
 * @example throw new AuthError(httpError);
 */
class AuthError extends ClientError {
  constructor(cause: HTTPError) {
    super(cause);
    this.name = "AuthError";
  }
}

/**
 * HTTP 404 not found errors.
 * @example throw new NotFoundError(httpError);
 */
class NotFoundError extends ClientError {
  constructor(cause: HTTPError) {
    super(cause);
    this.name = "NotFoundError";
  }
}

/**
 * HTTP 422 validation errors (invalid request data).
 * @example throw new ValidationError(httpError);
 */
class ValidationError extends ClientError {
  constructor(cause: HTTPError) {
    super(cause);
    this.name = "ValidationError";
  }
}

/**
 * HTTP 5xx server errors.
 * @example throw new ServerError(httpError);
 */
class ServerError extends HTTPError {
  constructor(cause: HTTPError) {
    super(cause.response, cause.request, cause.options);
    this.name = "ServerError";
  }
}

/**
 * Union type of all network-related error types.
 * Includes HTTP errors (auth, client, server), fetch failures, timeouts, and unknown errors.
 */
type NetworkError =
  | AuthError
  | ClientError
  | FetchError
  | HTTPError
  | NotFoundError
  | ServerError
  | TimeoutError
  | UnknownError
  | ValidationError;

/**
 * Type guard for strict network error instances (excludes generic HTTPError/TimeoutError).
 * @param error - Value to check
 * @returns True if error is a specific network error class instance
 * @example if (isNetworkErrorStrict(err)) { console.log(err.name); }
 */
function isNetworkErrorStrict(
  error: unknown,
): error is
  | AuthError
  | ClientError
  | FetchError
  | NotFoundError
  | ServerError
  | UnknownError
  | ValidationError {
  return (
    error instanceof ServerError ||
    error instanceof ClientError ||
    error instanceof AuthError ||
    error instanceof NotFoundError ||
    error instanceof ValidationError ||
    error instanceof FetchError ||
    error instanceof UnknownError
  );
}

/**
 * Type guard for Ky HTTP library errors.
 * @param error - Value to check
 * @returns True if error is HTTPError, TimeoutError, or fetch failure
 * @example if (isKyError(err)) { console.log('Ky error detected'); }
 */
function isKyError(
  error: unknown,
): error is HTTPError | TimeoutError | TypeError {
  return (
    error instanceof HTTPError ||
    error instanceof TimeoutError ||
    (error instanceof Error &&
      (error.message === "Load failed" || error.message === "Failed to fetch"))
  );
}

/**
 * Type guard for any network-related error.
 * @param error - Value to check
 * @returns True if error is any network error type
 * @example if (isNetworkError(err)) { handleNetworkError(err); }
 */
function isNetworkError(error: unknown): error is NetworkError {
  return isNetworkErrorStrict(error) || isKyError(error);
}

/**
 * Converts any error to a specific NetworkError type based on HTTP status.
 * Maps 401/403→AuthError, 404→NotFoundError, 422→ValidationError, 4xx→ClientError, 5xx→ServerError.
 * Idempotent: returns same error if already a NetworkError.
 * @param error - Any error value
 * @returns Specific NetworkError subclass
 * @example const netErr = toNetworkError(httpError);
 */
function toNetworkError(error: unknown): NetworkError {
  // This makes the function idempotent, so it can be called multiple times without creating new errors
  if (isNetworkErrorStrict(error)) {
    return error;
  }

  // We can't handle non-ky errors more specifically atm
  if (!isKyError(error)) {
    return toUnknownError(error);
  }

  if (error instanceof TimeoutError) {
    return error;
  }

  if (error instanceof HTTPError) {
    const status = error.response.status;
    switch (true) {
      case status === 401 || status === 403:
        return new AuthError(error);
      case status === 404:
        return new NotFoundError(error);
      case status === 422:
        return new ValidationError(error);
      case status >= 400 && status <= 499:
        return new ClientError(error);
      case status >= 500 && status <= 599:
        return new ServerError(error);
    }
  }

  return new FetchError(error);
}

/**
 * Laravel's `{ errors: { field: string[] } }` flattened to `{ field: "msg; msg" }`.
 * Sentry's extraErrorData normalizes three levels deep, so the nested arrays arrive as "[Array]".
 */
function flattenValidationErrors(body: unknown): Record<string, string> | null {
  if (
    typeof body !== "object" ||
    body === null ||
    !("errors" in body) ||
    typeof body.errors !== "object" ||
    body.errors === null
  ) {
    return null;
  }
  return Object.fromEntries(
    Object.entries(body.errors).map(([field, messages]) => [
      field,
      Array.isArray(messages) ? messages.join("; ") : String(messages),
    ]),
  );
}

type ClientErrorDetails = {
  responseBody?: string;
  responseJson?: unknown;
  responseReadError?: string;
  validationErrors?: Record<string, string>;
};

/** Attaches the response body to the error; extraErrorData sends its own properties to Sentry. */
async function attachResponseBody(error: ClientError) {
  const details = error as ClientError & ClientErrorDetails;
  try {
    const text = await error.response.clone().text();
    try {
      details.responseJson = JSON.parse(text);
    } catch {
      details.responseBody = text.slice(0, 1000);
      return;
    }
    const validationErrors = flattenValidationErrors(details.responseJson);
    if (validationErrors) {
      details.validationErrors = validationErrors;
    }
  } catch (readError) {
    details.responseReadError = String(readError);
  }
}

/**
 * Sends network errors to Sentry with smart categorization.
 * Server/timeout/fetch errors logged as warnings; client errors as exceptions with response JSON.
 * @param error - Error to send to Sentry
 * @param hint - Optional Sentry hint object
 * @param getSentryInstance - Sentry instance getter (default: getSentry)
 * @example networkErrorToSentry(error, { level: 'warning' });
 */
type CaptureArgs = Parameters<CaptureException>;
const networkErrorToSentry = (
  error: CaptureArgs[0],
  hint?: CaptureArgs[1],
  getSentryInstance = getSentry,
) => {
  const networkError = toNetworkError(error);
  if (
    networkError instanceof ServerError ||
    networkError instanceof TimeoutError ||
    networkError instanceof FetchError ||
    (networkError instanceof AuthError && networkError.response.status === 401)
  ) {
    getSentryInstance().captureLog(networkError, hint);
    return;
  }

  if (networkError instanceof ClientError) {
    void attachResponseBody(networkError).finally(() => {
      getSentryInstance().captureException(networkError, hint);
    });
    return;
  }

  getSentryInstance().captureException(networkError, hint);
};

/**
 * Wraps async function to return NetworkError instead of throwing.
 * Combines returnError + toNetworkError for functional error handling.
 * @param callback - Async function to wrap
 * @returns Function that returns NetworkError | Result
 * @example const safeFetch = returnNetworkError(fetchData);
 */
function returnNetworkError<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
): (...args: Args) => Promise<NetworkError | Return> {
  const wrappedCallback = returnError(callback);
  return async (...args) => {
    const result = await wrappedCallback(...args);
    return isError(result) ? toNetworkError(result) : result;
  };
}

/**
 * Wraps async function to return NetworkError and auto-report to Sentry.
 * Use for plain network calls outside of @pinia/colada.
 * Combines returnNetworkError + networkErrorToSentry for error handling with monitoring.
 * @param callback - Async function to wrap
 * @param sentryInstance - Optional custom Sentry instance
 * @returns Function that returns NetworkError | Result
 * @example const monitored = handleNetwork(apiCall);
 * @see {@link file://.ai/guidelines/frontend.md} for error handling patterns
 */
function handleNetwork<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
  sentryInstance?: () => SentryInstance,
): (...args: Args) => Promise<NetworkError | Return> {
  const wrappedCallback = returnNetworkError(callback);
  return async (...args) => {
    const result = await wrappedCallback(...args);
    if (isError(result)) {
      networkErrorToSentry(result, undefined, sentryInstance);
    }
    return result;
  };
}

/**
 * Wraps async function to throw NetworkError and auto-report to Sentry.
 * Use for @pinia/colada queries and mutations (colada handles thrown errors internally).
 * Combines handleNetwork + throwError for traditional try/catch error handling with monitoring.
 * @param callback - Async function to wrap
 * @param sentryInstance - Optional custom Sentry instance
 * @returns Function that throws NetworkError on failure
 * @example const query = handleQuery(fetchData); try { await query(); } catch(err) {}
 * @see {@link file://.ai/guidelines/frontend.md} for error handling patterns
 */
function handleQuery<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
  sentryInstance?: () => SentryInstance,
) {
  return throwError(handleNetwork(callback, sentryInstance));
}

const retryStatusCodes = new Set([408, 429, 500, 502, 503, 504]);

function isRetryable(error: unknown): boolean {
  if (error instanceof TimeoutError) return true;
  if (error instanceof HTTPError) {
    return retryStatusCodes.has(error.response.status);
  }
  return error instanceof TypeError;
}

async function withRetry<Result>(
  callback: () => Promise<Result>,
  limit = 2,
): Promise<Result> {
  async function attempt(remaining: number): Promise<Result> {
    try {
      return await callback();
    } catch (error) {
      if (remaining <= 0 || !isRetryable(error)) throw error;
      const delay = 300 * 2 ** (limit - remaining);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return attempt(remaining - 1);
    }
  }
  return attempt(limit);
}

export type { NetworkError };
export {
  AuthError,
  ClientError,
  FetchError,
  handleNetwork,
  handleQuery,
  HTTPError,
  isKyError,
  isNetworkError,
  networkErrorToSentry,
  NotFoundError,
  returnNetworkError,
  ServerError,
  TimeoutError,
  toNetworkError,
  UnknownError,
  ValidationError,
  withRetry,
};
